import { CssBaseline } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { FormattedMessage } from 'react-intl';
import { RouterProvider } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import InterpolatedSlicesDialog from './components/Dialogs/InterpolatedSlicesDialog';
import JobQueue from './components/JobQueue';
import FirefoxBlockPage from './containers/errors/FirefoxBlockPage';
import { UnexpectedErrorPage } from './containers/errors/RouteErrorBoundary';
import { useDomainEventHandlers } from './domain/events/handlers/useDomainEventHandlers';
import { IntlProvider } from './i18n';
import { createMsalInstance } from './lib/msal';
import router from './routes/router';
import { initializeSentry } from './sentry';
import './theme/fonts.css';
import './theme/index.css';
import ThemeProvider from './theme/ThemeProvider';
import AppConfig from './utils/appconfig';
import { initializeItkWasmPipelines } from './lib/elastix';

declare module 'notistack' {
    interface VariantOverrides {
        jobQueue: true;
    }
}

const queryClient = new QueryClient();
const root = createRoot(document.getElementById('root')!);
const msalDisabled = import.meta.env.VITE_MSAL_DISABLED === 'true';

// eslint-disable-next-line no-console
console.info(`Workspace+ version ${AppConfig.version}`);

// Initialize ITK-Wasm pipelines for image registration
initializeItkWasmPipelines().catch(console.error);

function isFirefox(): boolean {
    return (
        /firefox/i.test(navigator.userAgent) &&
        !/seamonkey/i.test(navigator.userAgent)
    );
}

const AppProvidersAndRouter = () => {
    useDomainEventHandlers();

    if (isFirefox()) {
        return (
            <IntlProvider>
                <ThemeProvider>
                    <CssBaseline />
                    <FirefoxBlockPage
                        title={<FormattedMessage id="firefoxNotSupported" />}
                        stack={
                            <FormattedMessage
                                id="detectedBrowser"
                                values={{ browser: navigator.userAgent }}
                            />
                        }
                    />
                </ThemeProvider>
            </IntlProvider>
        );
    }

    return (
        <IntlProvider>
            <ThemeProvider>
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                    <SnackbarProvider
                        classes={{
                            containerAnchorOriginBottomRight:
                                'snackbar-container-bottom-right-default',
                        }}
                        maxSnack={3}
                        preventDuplicate
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        Components={{
                            jobQueue: JobQueue,
                        }}
                    >
                        <ErrorBoundary
                            fallback={<UnexpectedErrorPage stack={undefined} />}
                        >
                            <RouterProvider router={router} />
                            <InterpolatedSlicesDialog />
                        </ErrorBoundary>
                    </SnackbarProvider>
                </QueryClientProvider>
            </ThemeProvider>
        </IntlProvider>
    );
};

const AppContent = (
    <RecoilRoot>
        <AppProvidersAndRouter />
    </RecoilRoot>
);

async function initializeAndRenderApp() {
    try {
        await AppConfig.initialize();
    } catch (error) {
        console.error('Failed to load app configuration:', error);
    }

    initializeSentry();

    if (msalDisabled) {
        root.render(<StrictMode>{AppContent}</StrictMode>);
        return;
    }

    if (!AppConfig.clientId || !AppConfig.authority) {
        console.debug('Authentication is disabled.');
        root.render(<StrictMode>{AppContent}</StrictMode>);
        return;
    }

    try {
        const { MsalProvider } = await import('@azure/msal-react');
        const msalInstance = await createMsalInstance();

        root.render(
            <StrictMode>
                <MsalProvider instance={msalInstance}>
                    {AppContent}
                </MsalProvider>
            </StrictMode>
        );
    } catch (error) {
        console.error('MSAL setup or initialization failed:', error);
        console.warn(
            'Proceeding without MSAL authentication due to an error during its setup.'
        );
        root.render(<StrictMode>{AppContent}</StrictMode>);
    }
}

initializeAndRenderApp();
